import React, { useEffect, useMemo, useState } from "react";
import { useReglages } from "../lib/useReglages";
import { construireAgenda, periodeLisible, TYPES_AGENDA } from "../lib/agenda";
import { SAISON } from "../lib/dates";
import { SPECTACLES } from "../pages/Spectacles";

/* L'année d'un coup d'œil : les douze mois de la saison (août → juillet), chaque jour
   coloré selon ce qui s'y passe. Vacances et jours fériés en rouge pâle, portes ouvertes en
   vert, stages en orange ; spectacles, auditions et workshops en pastilles. Un tap sur un
   jour dit ce qu'il y a ce jour-là. Le même calendrier sert aux familles, aux profs et à la
   direction : les dates viennent toutes de la console et du calendrier vaudois. */

const MOIS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août",
  "Septembre", "Octobre", "Novembre", "Décembre"];
const JOURS = ["L", "M", "M", "J", "V", "S", "D"];
const TYPES = { ...TYPES_AGENDA, rentree: { ...TYPES_AGENDA.rentree, label: "Reprise des cours" },
  workshop: { label: "Workshop heels", couleur: "#EC4899" } };
// Les périodes colorent le fond du jour ; les autres événements sont des pastilles.
const FOND = { fermeture: "#FEE2E2", portes_ouvertes: "#DCFCE7", stage: "#FEF3C7" };
const ORDRE_FOND = ["fermeture", "stage", "portes_ouvertes"];

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const lisible = (j) => {
  const d = new Date(`${j}T12:00:00`);
  return `${["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"][d.getDay()]} ${d.getDate()} ${MOIS[d.getMonth()].toLowerCase()}`;
};

/* `variante` : « app » (applications, deux mois par ligne, carte du jour en bas), « site »
   (page publique Dates importantes : calendrier sur toute la largeur, grandes cases, le
   détail d'un jour s'ouvre dans une carte en bas de l'écran) ; `large` = console (détail
   dans une colonne à droite). */
export const CalendrierAnnee = ({ testId = "calendrier-annee", large = false, variante = "app" }) => {
  const site = variante === "site";
  const { portes_ouvertes: po } = useReglages();
  const [workshops, setWorkshops] = useState([]);
  const [choisi, setChoisi] = useState(iso(new Date()));
  const [masques, setMasques] = useState([]);
  const [moisVu, setMoisVu] = useState(null);
  // Un jour touché s'ouvre dans une carte posée au-dessus de la barre d'onglets : elle ne
  // cache jamais le calendrier qu'on parcourt (comme une feuille iOS), et se ferme d'un tap.
  const [ouvert, setOuvert] = useState(false);
  const [bas, setBas] = useState(0);
  useEffect(() => {
    // Seulement une barre fixée EN BAS (applications) : le menu fixé en haut du site public
    // ne doit pas décaler la carte du jour.
    const nav = [...document.querySelectorAll("nav")].find((n) => getComputedStyle(n).position === "fixed"
      && n.getBoundingClientRect().bottom >= window.innerHeight - 4);
    setBas(nav ? nav.offsetHeight : 0);
  }, []);

  useEffect(() => {
    if (!ouvert) return undefined;
    const echap = (e) => { if (e.key === "Escape") setOuvert(false); };
    window.addEventListener("keydown", echap);
    return () => window.removeEventListener("keydown", echap);
  }, [ouvert]);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/workshops`).then((r) => (r.ok ? r.json() : null))
      .then((d) => setWorkshops((d?.seances || []).map((s) => ({
        id: `ws-${s.id}`, type: "workshop", debut: s.date, fin: s.date,
        titre: `${d.titre || "Workshop heels"} · ${s.debut}–${s.fin}`, detail: d.lieu || "",
      })))).catch(() => {});
  }, []);

  const evts = useMemo(() => [...construireAgenda(po, SPECTACLES), ...workshops]
    .filter((e) => !masques.includes(e.type)), [po, workshops, masques]);

  // Ce qui se passe chaque jour de la saison.
  const parJour = useMemo(() => {
    const out = {};
    evts.forEach((e) => {
      const d = new Date(`${e.debut}T12:00:00`);
      const fin = e.fin || e.debut;
      for (let k = 0; k < 400 && iso(d) <= fin; k += 1) {
        (out[iso(d)] = out[iso(d)] || []).push(e);
        d.setDate(d.getDate() + 1);
      }
    });
    return out;
  }, [evts]);

  const annee = Number(SAISON.rentree.iso.slice(0, 4));
  const mois = Array.from({ length: 12 }, (_, k) => new Date(annee, 7 + k, 1));
  // Site, sur téléphone : un mois à la fois (flèches), en commençant par le mois en cours.
  const indexDuJour = Math.max(0, mois.findIndex((m) => m.getFullYear() === new Date().getFullYear()
    && m.getMonth() === new Date().getMonth()));
  const aujourdhui = iso(new Date());
  const duJour = parJour[choisi] || [];
  const types = Object.keys(TYPES);

  const detail = (
    <>
      <div className="flex items-center justify-between gap-3">
        <div className="text-[13px] font-semibold capitalize">{choisi === aujourdhui ? "Aujourd'hui · " : ""}{lisible(choisi)}</div>
        {ouvert && (
          <button onClick={() => { setOuvert(false); setChoisi(aujourdhui); }} aria-label="Fermer" data-testid="calendrier-fermer"
            className="w-8 h-8 -mr-1 grid place-items-center rounded-full text-[18px] leading-none"
            style={{ background: "#F1F5F9", color: "#475569" }}>×</button>
        )}
      </div>
      {duJour.length ? duJour.map((e) => (
        <div key={e.id} className="flex items-start gap-2 mt-2">
          <span className="w-2.5 h-2.5 rounded-full mt-1.5 shrink-0" style={{ background: TYPES[e.type]?.couleur || "#94A3B8" }} />
          <div className="min-w-0">
            <div className="text-[14.5px] leading-snug">{e.titre}</div>
            <div className="text-[12px] text-[var(--muted)]">{periodeLisible(e.debut, e.fin)}{e.detail ? ` · ${e.detail}` : ""}</div>
          </div>
        </div>
      )) : <div className="text-[13px] text-[var(--muted)] mt-1">Rien de particulier : cours selon le planning (ou week-end).</div>}
    </>
  );

  return (
    <div className="space-y-4" data-testid={testId}>
      <div className="flex flex-wrap gap-1.5">
        {types.map((t) => {
          const off = masques.includes(t);
          return (
            <button key={t} onClick={() => setMasques(off ? masques.filter((x) => x !== t) : [...masques, t])}
              className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-medium bg-white"
              style={{ border: "1px solid var(--border)", opacity: off ? 0.4 : 1 }} aria-pressed={!off}>
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: FOND[t] ? FOND[t] : TYPES[t].couleur, border: FOND[t] ? `1.5px solid ${TYPES[t].couleur}` : "none" }} />
              {TYPES[t].label}
            </button>
          );
        })}
      </div>

      {site && (
        <p className="text-sm text-[var(--muted)]">Clique sur un jour pour voir ce qui s'y passe.</p>
      )}
      {site && (
        <div className="flex items-center justify-between sm:hidden" data-testid="calendrier-mois-nav">
          <button onClick={() => setMoisVu(Math.max(0, (moisVu ?? indexDuJour) - 1))} disabled={(moisVu ?? indexDuJour) === 0}
            aria-label="Mois précédent" className="w-11 h-11 rounded-full border bg-white text-xl disabled:opacity-30"
            style={{ borderColor: "var(--border)" }}>‹</button>
          <button onClick={() => setMoisVu(indexDuJour)} className="text-center leading-tight"
            aria-label="Revenir au mois en cours">
            <span className="block text-lg font-semibold">
              {MOIS[mois[moisVu ?? indexDuJour].getMonth()]} {mois[moisVu ?? indexDuJour].getFullYear()}
            </span>
            {(moisVu ?? indexDuJour) !== indexDuJour && (
              <span className="block text-[12px] underline text-[var(--muted)]">revenir à aujourd'hui</span>
            )}
          </button>
          <button onClick={() => setMoisVu(Math.min(11, (moisVu ?? indexDuJour) + 1))} disabled={(moisVu ?? indexDuJour) === 11}
            aria-label="Mois suivant" className="w-11 h-11 rounded-full border bg-white text-xl disabled:opacity-30"
            style={{ borderColor: "var(--border)" }}>›</button>
        </div>
      )}
      {!large && !site && !ouvert && (
        <div className="rounded-[22px] bg-white p-4" style={{ border: "1px solid var(--border)" }} data-testid="calendrier-jour">
          {detail}
          <div className="text-[11.5px] text-[var(--muted)] mt-2">Touche un jour pour voir ce qui s'y passe.</div>
        </div>
      )}
      {!large && ouvert && (
        <div className={`fixed inset-x-0 px-3 pb-2 ${site ? "z-[65] pb-4" : "z-30"}`} style={{ bottom: bas }}>
          <div className={`${site ? "max-w-2xl p-5 md:p-6" : "max-w-xl p-4"} mx-auto rounded-[22px] bg-white max-h-[45vh] overflow-y-auto`}
            style={{ border: "1px solid var(--border)", boxShadow: "0 -4px 24px rgba(15,27,45,0.16)" }}
            data-testid="calendrier-jour">
            {detail}
          </div>
        </div>
      )}

      {/* Sur ordinateur : le détail du jour dans une colonne à droite, toujours visible
          à côté du calendrier (une carte flottante en bas paraissait posée au hasard). */}
      <div className={large ? "grid lg:grid-cols-[minmax(0,1fr)_300px] gap-4 items-start" : ""}>
      <div className={`grid ${site ? "gap-4 sm:grid-cols-2 lg:grid-cols-3" : large ? "gap-3 grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4" : "gap-3 grid-cols-2"}`}
        style={{ paddingBottom: ouvert && !large ? 200 : 0 }}>
        {mois.map((m, km) => {
          const premier = (m.getDay() + 6) % 7;
          const nb = new Date(m.getFullYear(), m.getMonth() + 1, 0).getDate();
          const cases = [...Array(premier).fill(null), ...Array.from({ length: nb }, (_, k) => new Date(m.getFullYear(), m.getMonth(), k + 1))];
          return (
            <div key={`${m.getFullYear()}-${m.getMonth()}`} style={{ border: "1px solid var(--border)" }}
              className={`rounded-2xl bg-white ${site ? `p-4 ${km === (moisVu ?? indexDuJour) ? "" : "hidden sm:block"}` : "p-2.5"}`}>
              <div className={`${site ? "hidden sm:block text-base mb-2.5" : "text-[12.5px] mb-1.5"} font-semibold px-0.5`}>{MOIS[m.getMonth()]} {m.getFullYear()}</div>
              <div className={`grid grid-cols-7 text-center ${site ? "gap-y-1" : "gap-y-0.5"}`}>
                {JOURS.map((j, k) => <div key={k} className={`${site ? "text-[11px] pb-1" : "text-[9px]"} font-semibold text-[var(--muted)]`}>{j}</div>)}
                {cases.map((d, k) => {
                  if (!d) return <div key={`v${k}`} />;
                  const j = iso(d);
                  const ev = parJour[j] || [];
                  const fond = ORDRE_FOND.find((t) => ev.some((e) => e.type === t));
                  const points = ev.filter((e) => !FOND[e.type]);
                  const auj = j === aujourdhui;
                  return (
                    <button key={j} onClick={() => { setChoisi(j); setOuvert(true); }} aria-label={lisible(j)}
                      className={`relative tabular-nums rounded-md ${site ? "h-9 text-[13.5px] leading-9 hover:ring-2 hover:ring-[#0F1B2D]/15" : "h-[22px] text-[10.5px] leading-[22px]"}`}
                      style={{ background: fond ? FOND[fond] : undefined,
                        outline: ouvert && j === choisi && !auj ? "2px solid #0F1B2D" : "none", outlineOffset: -1 }}>
                      {/* Aujourd'hui : un rond plein, comme le calendrier de l'iPhone. */}
                      {/* Spectacle, audition, workshop : la date est entourée de sa couleur,
                          bien visible (un petit point passait inaperçu). */}
                      <span className={`inline-grid place-items-center rounded-full leading-none ${site ? "w-8 h-8" : "w-[19px] h-[19px]"}`}
                        style={auj ? { background: "#DC2626", color: "#fff", fontWeight: 700 }
                          : points.length ? { background: `${TYPES[points[0].type]?.couleur || "#64748B"}22`,
                            boxShadow: `inset 0 0 0 2px ${TYPES[points[0].type]?.couleur || "#64748B"}`,
                            color: TYPES[points[0].type]?.couleur || "var(--text)", fontWeight: 700 }
                          : { color: d.getDay() === 0 || d.getDay() === 6 ? "#94A3B8" : "var(--text)" }}>
                        {d.getDate()}
                      </span>
                      {new Set(points.map((e) => e.type)).size > 1 && (
                        <span className="absolute right-0 top-0 w-[6px] h-[6px] rounded-full"
                          style={{ background: TYPES[points.find((e) => e.type !== points[0].type).type]?.couleur }} />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      {large && (
        <aside className="rounded-[22px] bg-white p-4 lg:sticky lg:top-24" style={{ border: "1px solid var(--border)" }} data-testid="calendrier-jour">
          {detail}
          {!ouvert && <div className="text-[11.5px] text-[var(--muted)] mt-2">Clique un jour pour voir ce qui s'y passe.</div>}
        </aside>
      )}
      </div>
    </div>
  );
};
